import React, { useState, useCallback, useMemo } from 'react'
import { Formik } from 'formik'
import tw from 'twin.macro'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { Button } from 'src/components/shared/Button'
import { InviteTeamMemberFormValues } from './InviteTeamMemberForm.interface'
import { InviteTeamMemberSchema } from './InviteTeamMemberForm.validator'
import { handleApiError } from 'src/utils/errorHandlers'
import { useSelector, useDispatch } from 'react-redux'
import { getPartnerId } from 'src/selectors/user'
import { AddMarkets } from 'src/components/markets/AddMarkets'
import { Market } from 'src/interfaces/market'
import { TeamRole } from 'src/interfaces/team'
import { showToast } from 'src/utils/toast'
import { SelectTeamRole } from '../../select-team-role/SelectTeamRole'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchTeamMemberInvitesCount, SendInviteToTeamMember } from 'src/constants/actionTypes'
import { getMarkets } from 'src/selectors/market'
import { SendMajor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { TeamTableTab } from 'src/constants/team/enums'

interface SendInviteToTeamMemberRequest {
  email: string
  firstName: string
  lastName: string
  roleId: string
  allMarkets: boolean
  marketIds: string[]
}

const initialValues: InviteTeamMemberFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  roleId: '',
  allMarkets: false,
  marketIds: [],
}

interface Props {
  closeInviteTeamMember(): void
  setSelectedTab: (tab: TeamTableTab) => void
  onRefreshTeamMemberInvites: () => void
}

export const InviteTeamMemberForm = ({
  closeInviteTeamMember,
  setSelectedTab,
  onRefreshTeamMemberInvites,
}: Props) => {
  const dispatch = useDispatch()

  const partnerId = useSelector(getPartnerId)
  const markets = useSelector(getMarkets)

  const [isLoading, setIsLoading] = useState(false)
  const [selectedMarkets, setSelectedMarkets] = useState<Market[]>([])
  const [openModalId, setOpenModalId] = useState<string | null>(null)
  const [selectedTeamRole, setSelectedTeamRole] = useState<TeamRole | null>(null)

  const allMarketsSelected = useMemo(
    () => selectedMarkets.length >= markets.length,
    [markets, selectedMarkets],
  )

  const handleSelectMarket = useCallback(async (newMarket: Market) => {
    setSelectedMarkets((prev) => [...prev, newMarket])
  }, [])

  const handleRemoveMarket = useCallback(async (removedMarket: Market) => {
    setSelectedMarkets((prev) => prev.filter((market) => market.id !== removedMarket.id))
  }, [])

  const handleTeamRoleSelect = useCallback((role: TeamRole) => {
    setSelectedTeamRole(role)
    setOpenModalId(null)
  }, [])

  const fetchTeamMemberInvitesCount = useCallback(() => {
    if (!partnerId) {
      return
    }

    dispatch(FetchTeamMemberInvitesCount.request())
  }, [partnerId])

  const handleFormSubmit = async (values: InviteTeamMemberFormValues) => {
    if (!partnerId) {
      return
    }

    if (!selectedTeamRole) {
      showToast('Select user role', { variant: 'error' })

      return
    }

    if (!selectedMarkets.length) {
      showToast('Select at least one market', { variant: 'error' })

      return
    }

    const payload: SendInviteToTeamMemberRequest = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      roleId: selectedTeamRole.id,
      allMarkets: markets.length === selectedMarkets.length,
      marketIds: selectedMarkets.map((market) => market.id),
    }

    setIsLoading(true)

    try {
      await createAsyncAction(dispatch, SendInviteToTeamMember.request(payload))

      showToast(`Invitation sent successfully to "${values.email}"`, {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })

      onRefreshTeamMemberInvites()
      fetchTeamMemberInvitesCount()

      closeInviteTeamMember()
      setSelectedTab(TeamTableTab.Invites)
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={InviteTeamMemberSchema}
      onSubmit={handleFormSubmit}
    >
      {({ handleSubmit }) => (
        <div css={tw`h-full flex flex-col min-h-0`}>
          <div css={tw`px-4 pt-4 flex-1 min-h-0 overflow-y-auto`}>
            <div css={tw`grid grid-cols-1 gap-4 mb-4`}>
              <FormikTextField
                name="firstName"
                label="First name"
                placeholder="Enter manager's first name"
                css={tw`w-full`}
              />
              <FormikTextField
                name="lastName"
                label="Last name"
                placeholder="Enter manager's last name"
                css={tw`w-full`}
              />
              <FormikTextField
                name="email"
                label="Email"
                placeholder="Enter manager's email"
                css={tw`w-full`}
              />
              <SelectTeamRole
                selectedTeamRole={selectedTeamRole}
                handleTeamRoleSelect={handleTeamRoleSelect}
                modalId="team-details"
                openModalId={openModalId}
                setOpenModalId={setOpenModalId}
              />
            </div>
            <AddMarkets
              markets={selectedMarkets}
              className={tw`pb-4`}
              onSelectMarket={handleSelectMarket}
              onRemoveMarket={handleRemoveMarket}
              allMarketsSelected={allMarketsSelected}
            />
          </div>
          <div
            css={tw`flex-shrink-0 flex flex-col justify-end items-end border-0 border-t border-solid border-[#EDEDED] mx-4 py-4`}
          >
            <Button
              variant="contained"
              size="small"
              loading={isLoading}
              onClick={() => handleSubmit()}
              endIcon={<SendMajor width={20} fill={colors.BASICS_WHITE} />}
              className={tw`w-[160px]`}
            >
              Send invite
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}
